import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, Heart, Users, ArrowRight, Sparkles } from 'lucide-react';
export const SchoolsHighlightSection = () => {
  const pillars = [{
    icon: Heart,
    title: 'Identity & Belonging',
    body: 'Especially meaningful for Punjabi Sikh students.'
  }, {
    icon: BookOpen,
    title: 'Sikh History, Made Vivid',
    body: 'Story-led, immersive entry into a powerful chapter of history.'
  }, {
    icon: Users,
    title: 'Cultural Understanding for All',
    body: 'Empathy, literacy, and connection across communities.'
  }];
  return <section id="schools" className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-1627">
      {/* atmospheric background */}
      <div className="absolute inset-0" data-id="element-1628">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-10" data-id="element-1629" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" data-id="element-1630" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-1631" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-1632">
        <div className="max-w-6xl mx-auto" data-id="element-1633">
          {/* ---------- Heading ---------- */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-14 max-w-3xl mx-auto" data-id="element-1634">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-6" data-id="element-1635">
              <GraduationCap className="w-3.5 h-3.5 text-amber-400" data-id="element-1636" />
              <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-light" data-id="element-1637">
                For Schools &amp; Curriculum Partners
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 leading-[1.05] tracking-tight" data-id="element-1638">
              Bring Sikh History to{' '}
              <span className="text-amber-400" data-id="element-1639">Life for Students</span>
            </h2>
            <div className="w-20 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-1640" />
            <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-1641">
              An immersive Sikh history learning experience that supports
              identity, belonging, cultural understanding, and student
              engagement — designed for middle school and high school groups.
            </p>
          </motion.div>

          {/* ---------- Three Pillars ---------- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-1642">
            {pillars.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors p-6 bg-gradient-to-b from-amber-950/8 to-transparent" data-id="element-1643">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-1644" />
                <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-4" data-id="element-1645">
                  <p.icon className="w-4 h-4 text-amber-400" data-id="element-1646" />
                </div>
                <h3 className="font-serif text-lg text-white mb-2 leading-tight" data-id="element-1647">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-1648">
                  {p.body}
                </p>
              </motion.div>)}
          </div>

          {/* ---------- Grade-level + CTA strip ---------- */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative border border-amber-400/25 bg-gradient-to-br from-amber-950/15 via-black/40 to-black p-6 sm:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10" data-id="element-1649">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-1650" />

            <div className="flex-1" data-id="element-1651">
              <div className="flex items-center gap-3 mb-3" data-id="element-1652">
                <Sparkles className="w-4 h-4 text-amber-400" data-id="element-1653" />
                <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-light" data-id="element-1654">
                  Middle School · High School · Curriculum Partners
                </span>
              </div>
              <p className="font-serif text-xl sm:text-2xl text-white leading-snug max-w-2xl" data-id="element-1655">
                Group visits, educator previews, curriculum enrichment, and
                pilot partnerships — designed to{' '}
                <span className="text-amber-400" data-id="element-1656">complement and enrich</span>{' '}
                existing learning.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-1657">
              <Link to="/schools" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_30px_rgba(212,175,55,0.25)]" data-id="element-1658">
                Explore the School Program
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-1659" />
              </Link>
              <Link to="/schools#interest-form" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-1660">
                Request a Conversation
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};